import { ZoneContext } from 'context/ZoneContext';
import { useCallback, useContext, useEffect } from 'react';
import { useRegionContext } from 'context/RegionContext';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';

export const useMapInteractionEvents = () => {
    const { selectedStation, zone, selectedPath, expandedStation } = useContext(ZoneContext);
    const { region } = useRegionContext();
    const { captureMantaCareEvent } = useAnalytics();

    const getMapMetaData = () => ({
        region: {
            id: region.id,
            name: region.name
        },
        map: { name: region.map.name },
        zone: { name: zone.name }
    });

    const triggerZoneSelectionEvent = useCallback(
        () => captureMantaCareEvent({ type: 'map', action: 'zone_selected', metadata: getMapMetaData() }),
        [zone.name]
    );

    useEffect(() => {
        triggerZoneSelectionEvent();
    }, [triggerZoneSelectionEvent]);

    const triggerStationSelectionEvent = useCallback(() => {
        if (!selectedStation) return;

        captureMantaCareEvent({
            type: 'map',
            action: 'station_selected',
            metadata: {
                ...getMapMetaData(),
                station: {
                    id: selectedStation.id,
                    name: selectedStation.name,
                    type: selectedStation.type,
                    description: selectedStation.description
                }
            }
        });
    }, [selectedStation]);

    useEffect(() => {
        triggerStationSelectionEvent();
    }, [triggerStationSelectionEvent]);

    const triggerPathSelectionEvent = useCallback(() => {
        if (!selectedPath) return;

        const source = selectedPath.source;
        const target = selectedPath.target;

        captureMantaCareEvent({
            type: 'map',
            action: 'path_selected',
            metadata: {
                ...getMapMetaData(),
                source: {
                    id: source.id,
                    name: source.name,
                    type: source.type,
                    description: source.description
                },
                target: {
                    id: target.id,
                    name: target.name,
                    type: target.type,
                    description: target.description
                },
                description: selectedPath.description || ''
            }
        });
    }, [selectedPath]);

    useEffect(() => {
        triggerPathSelectionEvent();
    }, [triggerPathSelectionEvent]);

    const triggerExpandedStationEvent = useCallback(() => {
        if (!expandedStation) return;

        captureMantaCareEvent({
            type: 'map',
            action: 'station_expanded',
            metadata: {
                ...getMapMetaData(),
                station: {
                    id: expandedStation.id,
                    name: expandedStation.name,
                    type: expandedStation.type,
                    description: expandedStation.description
                }
            }
        });
    }, [expandedStation]);

    useEffect(() => {
        triggerExpandedStationEvent();
    }, [triggerExpandedStationEvent]);
};

export const useMapOpenEvents = () => {
    const { zone } = useContext(ZoneContext);
    const { region } = useRegionContext();
    const { captureMantaCareEvent } = useAnalytics();

    const getMapMetaData = () => ({
        region: {
            id: region.id,
            name: region.name
        },
        map: { name: region.map.name },
        zone: { name: zone.name }
    });

    const triggerMapOpenEvent = useCallback(() => {
        if (!region.name) return;
        captureMantaCareEvent({ type: 'map', action: 'map_open', metadata: getMapMetaData() });
    }, [region.name]);

    useEffect(() => {
        triggerMapOpenEvent();
    }, [triggerMapOpenEvent]);
};
